import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import Mascot from "@/components/Mascot";
import { InstallPrompt } from "@/components/InstallPrompt";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ManifestManager } from "@/lib/manifest";

import Login from "@/pages/Login";
import Layout from "@/components/Layout";

import GuruDashboard from "@/pages/guru/Dashboard";
import GuruStudents from "@/pages/guru/Students";
import GuruSubjects from "@/pages/guru/Subjects";
import GuruAssignments from "@/pages/guru/Assignments";
import GuruAttendance from "@/pages/guru/Attendance";
import GuruReview from "@/pages/guru/ReviewCenter";
import GuruReports from "@/pages/guru/Reports";
import GuruGames from "@/pages/guru/GamePanel";

import MuridDashboard from "@/pages/murid/Dashboard";
import MuridAttendance from "@/pages/murid/Attendance";
import MuridAssignments from "@/pages/murid/Assignments";
import MuridAssignmentDetail from "@/pages/murid/AssignmentDetail";
import MuridGrades from "@/pages/murid/Grades";
import MuridGame from "@/pages/murid/GamePlay";
import SubjectDetail from "@/pages/guru/SubjectDetail";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminPanel from "@/pages/admin/AdminPanel";

function Splash() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#FAF9F5]">
      <Mascot size={80} className="animate-float" />
      <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
    </div>
  );
}

function Protected({ role, roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  const allowed = roles || (role ? [role] : null);
  if (!user) return <Navigate to={allowed && allowed.includes("admin") ? "/admin" : "/"} replace />;
  if (allowed && !allowed.includes(user.role)) return <Navigate to={homePath(user.role)} replace />;
  return children;
}

function homePath(role) {
  if (role === "admin" || role === "operator") return "/admin/panel";
  return role === "guru" ? "/guru" : "/murid";
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={homePath(user.role)} replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <ManifestManager />
            <Routes>
              <Route path="/" element={<RootRedirect />} />

              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/panel" element={<Protected roles={["admin", "operator"]}><AdminPanel /></Protected>} />

              <Route element={<Protected role="guru"><Layout /></Protected>}>
                <Route path="/guru" element={<GuruDashboard />} />
                <Route path="/guru/murid" element={<GuruStudents />} />
                <Route path="/guru/mapel" element={<GuruSubjects />} />
                <Route path="/guru/mapel/:id" element={<SubjectDetail />} />
                <Route path="/guru/tugas" element={<GuruAssignments />} />
                <Route path="/guru/absensi" element={<GuruAttendance />} />
                <Route path="/guru/review" element={<GuruReview />} />
                <Route path="/guru/laporan" element={<GuruReports />} />
                <Route path="/guru/permainan" element={<GuruGames />} />
              </Route>

              <Route element={<Protected role="murid"><Layout /></Protected>}>
                <Route path="/murid" element={<MuridDashboard />} />
                <Route path="/murid/absensi" element={<MuridAttendance />} />
                <Route path="/murid/tugas" element={<MuridAssignments />} />
                <Route path="/murid/tugas/:id" element={<MuridAssignmentDetail />} />
                <Route path="/murid/nilai" element={<MuridGrades />} />
                <Route path="/murid/main" element={<MuridGame />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <InstallPrompt />
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
